import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LEARN } from "../../theme";
import { useBreakpoint } from "../../shell/LearnShell";
import { ConfettiBurst } from "../../feedback/Celebration";
import {
  PopIn,
  RoundCounter,
  ShakeWrap,
  SpeakerIcon,
  Tile,
  gameShell,
  itemKey,
  pieceButtonProps,
  seededShuffle,
  useCompleteOnce,
  useDragAndTap,
  useLetterAccent,
  useWrongFlash,
} from "./engineUtils";

/**
 * Picture match: hear a word, then tap the picture — or drag the word token
 * onto it. A line follows the token to the picture it hovers and locks in
 * when the match is right. Completes after `roundSize` (default 5) rounds.
 */
export default function PictureMatchGame({ letter, config, speak, replay, sfx, onComplete, onWrong, reduceMotion }) {
  const finish = useCompleteOnce(onComplete);
  const { wrongId, flash } = useWrongFlash();
  const bp = useBreakpoint();
  const compact = bp === "xs";
  const { accent: letterAccent } = useLetterAccent(letter);
  const bank = useMemo(
    () => seededShuffle(config.items || config.targetItems || [], 33),
    [config.items, config.targetItems]
  );
  const rounds = Math.min(config.roundSize || 5, Math.max(bank.length, 1));
  const perTurn = Math.min(6, Math.max(2, config.choicesPerTurn || 6));
  const [ri, setRi] = useState(0);
  const [solved, setSolved] = useState(null); // itemKey of the matched tile
  const [line, setLine] = useState(null);     // { x1,y1,x2,y2, tone }
  const [burst, setBurst] = useState(0);
  const rootRef = useRef(null);
  const tokenRef = useRef(null);
  const tileRefs = useRef(new Map());
  const timers = useRef([]);
  const target = bank[ri % Math.max(bank.length, 1)];

  const choices = useMemo(() => {
    if (!target) return [];
    const others = seededShuffle(bank.filter((x) => x.word !== target.word), 40 + ri).slice(0, perTurn - 1);
    return seededShuffle([target, ...others], 50 + ri);
  }, [bank, perTurn, ri, target]);

  const later = useCallback((fn, ms) => { const t = setTimeout(fn, ms); timers.current.push(t); return t; }, []);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const spokenPrompt = useCallback(
    (word) => (config.spokenPromptTemplate || "Find the {word}.").replace("{word}", word),
    [config.spokenPromptTemplate]
  );

  useEffect(() => {
    if (!target) return;
    setSolved(null);
    setLine(null);
    later(() => speak?.(spokenPrompt(target.word)), ri === 0 ? 900 : 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ri]);

  const answer = useCallback(
    (it) => {
      if (solved || !target) return false;
      if (it.word !== target.word) {
        flash(itemKey(it));
        onWrong?.();
        return false;
      }
      sfx?.("correct");
      setSolved(itemKey(it));
      setBurst((b) => b + 1);
      speak?.(it.word, { interrupt: true });
      later(() => {
        const next = ri + 1;
        if (next >= rounds) finish();
        else setRi(next);
      }, reduceMotion ? 350 : 1100);
      return true;
    },
    [finish, flash, later, onWrong, reduceMotion, ri, rounds, sfx, solved, speak, target]
  );

  const onDrop = useCallback(
    ({ payload, targetId, targetData }) => {
      if (!targetId || !targetData?.item) return false;
      return answer(targetData.item);
    },
    [answer]
  );
  const drag = useDragAndTap({ reduceMotion, sfx, onDrop });

  // Connecting line: token centre → hovered (or solved) tile centre, in root coords.
  const lineTo = drag.hoverId || solved;
  useLayoutEffect(() => {
    const root = rootRef.current;
    const tok = tokenRef.current;
    const tile = lineTo ? tileRefs.current.get(lineTo) : null;
    if (!root || !tok || !tile) { setLine(null); return; }
    const r = root.getBoundingClientRect();
    const a = tok.getBoundingClientRect();
    const b = tile.getBoundingClientRect();
    setLine({
      x1: a.left + a.width / 2 - r.left, y1: a.top + a.height / 2 - r.top,
      x2: b.left + b.width / 2 - r.left, y2: b.top + b.height / 2 - r.top,
      tone: solved ? LEARN.color.mint : LEARN.color.blue,
    });
  }, [lineTo, solved]);

  if (!target) return <div style={gameShell.root} />;

  const tileSize = `min(calc(var(--tile) * 1.55), calc((var(--arena-w) - 64px) / 3))`;
  const tokenStyle = {
    display: "inline-flex", alignItems: "center", gap: 10,
    height: "calc(var(--tile) * 0.8)", padding: "0 calc(var(--tile) * 0.28) 0 calc(var(--tile) * 0.18)",
    borderRadius: 999, background: LEARN.color.blue, color: "#fff",
    fontFamily: LEARN.font, fontWeight: 700, fontSize: compact ? 20 : 24, lineHeight: 1,
    boxShadow: LEARN.shadow.sticker(LEARN.color.blueDeep),
  };
  const renderGhost = (p) => (
    <div style={{ ...tokenStyle, boxShadow: LEARN.shadow.float }}>
      <span style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.22)", display: "grid", placeItems: "center" }}><SpeakerIcon /></span>
      {p.word}
    </div>
  );

  return (
    <div ref={rootRef} style={{ ...gameShell.root, gap: compact ? 12 : 18, paddingTop: compact ? 30 : 0 }}>
      <ConfettiBurst key={burst} active={burst > 0} originY={40} count={22} reduceMotion={reduceMotion} />
      {line && (
        <svg aria-hidden="true" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 3, overflow: "visible" }}>
          <motion.line
            x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2}
            stroke={line.tone} strokeWidth={solved ? 8 : 6} strokeLinecap="round"
            strokeDasharray={solved ? undefined : "2 14"}
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 0.95 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
          />
          <circle cx={line.x2} cy={line.y2} r={solved ? 12 : 8} fill={line.tone} opacity={0.9} />
        </svg>
      )}
      <RoundCounter done={ri} total={rounds} accent={letterAccent} />

      {/* Word token: tap to hear, drag onto a picture */}
      <div style={{ display: "flex", justifyContent: "center", flexShrink: 0 }}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={`${ri}-${target.word}`}
            initial={reduceMotion ? false : { scale: 0.6, opacity: 0, y: -12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={reduceMotion ? undefined : { scale: 0.7, opacity: 0, transition: { duration: 0.16 } }}
            transition={{ type: "spring", stiffness: 380, damping: 22 }}
          >
            <button
              ref={tokenRef}
              {...pieceButtonProps({
                dragProps: solved ? null : drag.dragProps,
                payload: target,
                render: renderGhost,
                label: `Hear ${target.word}`,
                onTap: () => { sfx?.("pop", { volume: 0.5 }); (replay || speak)?.(spokenPrompt(target.word)); },
                style: { borderRadius: 999, cursor: solved ? "default" : "grab" },
              })}
            >
              <div style={{ ...tokenStyle, background: solved ? LEARN.color.mint : LEARN.color.blue, boxShadow: LEARN.shadow.sticker(solved ? LEARN.color.mintDeep : LEARN.color.blueDeep), transition: "background 200ms ease" }}>
                <span style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.22)", display: "grid", placeItems: "center" }}><SpeakerIcon /></span>
                {target.word}
              </div>
            </button>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Picture choices */}
      <div
        style={{
          flex: 1, minHeight: 0, overflowY: "auto",
          display: "grid",
          gridTemplateColumns: `repeat(${Math.min(3, choices.length)}, ${tileSize})`,
          gridAutoRows: tileSize,
          gap: compact ? 10 : 16, justifyContent: "center", alignContent: "center",
        }}
        aria-label="Pictures"
      >
        {choices.map((it, i) => {
          const key = itemKey(it);
          const hover = drag.hoverId === key;
          const isSolved = solved === key;
          return (
            <PopIn key={`${ri}-${key}`} index={i} reduceMotion={reduceMotion} style={{ width: "100%", height: "100%" }}>
              <ShakeWrap shake={wrongId === key} reduceMotion={reduceMotion} style={{ borderRadius: "calc(var(--tile) * 0.3)", width: "100%", height: "100%" }}>
                <button
                  ref={(el) => { drag.registerTarget(key, el, { item: it }); if (el) tileRefs.current.set(key, el); else tileRefs.current.delete(key); }}
                  type="button"
                  className="learn-piece"
                  aria-label={it.word}
                  onClick={() => { sfx?.("pop", { volume: 0.4 }); answer(it); }}
                  style={{ background: "transparent", border: "none", padding: 0, width: "100%", height: "100%", cursor: "pointer", borderRadius: "calc(var(--tile) * 0.3)" }}
                >
                  <motion.div
                    animate={isSolved && !reduceMotion ? { scale: [1, 1.12, 1], rotate: [0, -3, 3, 0] } : { scale: hover ? 1.06 : 1 }}
                    transition={{ duration: 0.4 }}
                    style={{ width: "100%", height: "100%" }}
                  >
                    <Tile
                      letter={letter}
                      assetName={it.assetName}
                      alt={it.word}
                      selected={hover}
                      solved={isSolved}
                      dim={Boolean(solved) && !isSolved}
                      lift={false}
                      fill
                      style={{ borderRadius: "calc(var(--tile) * 0.3)" }}
                    />
                  </motion.div>
                </button>
              </ShakeWrap>
            </PopIn>
          );
        })}
      </div>
      <drag.GhostLayer />
    </div>
  );
}
